import React, { forwardRef, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import {
  Button,
  Checkbox,
  Input,
  Slider,
  Card,
  Divider,
  CardContent,
  FormControlLabel,
  MenuItem,
  InputLabel,
  FormControl,
  Select,
  IconButton,
  Tooltip,
  Grid,
  Typography,
  Snackbar,
  Alert as MuiAlert,
  TextField,
} from "@mui/material";

import InfoIcon from "@mui/icons-material/Info";
import { useTranslation } from "react-i18next";
import ModelCards from "./ModelCards";
import { fetchData, postData } from "../../utils/api";
import { UserContext } from "../../App";
import AdvancedOptionsSection from "./AdvancedOptionsSection";

// eslint-disable-next-line react/display-name
const Alert = forwardRef((props, ref) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />
));

export default function Trainer() {
  const theme = useTheme();
  // init internationalization
  const { t } = useTranslation();
  // needed for routing to pipeline site
  const navigate = useNavigate();
  // get data from current dataset + Experiment
  const {
    datasets,
    currentDatasetIdx,
    experiments,
    currentExperimentIdx,
    currentExperimentProgress,
  } = useContext(UserContext);

  const userEmail = window.localStorage.getItem("automl-email") || "";
  const showCheckbox = userEmail.endsWith("zsw-bw.de");

  const [selectedRun, setSelectedRun] = useState(t("trainer.DropdownRunsNew"));
  const [runsNames, setRunsNames] = useState([]);
  const [hpOptimizers, setHpOptimizers] = useState([]);
  const [dataBalancers, setDataBalancers] = useState([]);
  const [dataBalancerOptions, setDataBalancerOptions] = useState([]);
  const [selectedOptimizer, setSelectedOptimizer] = useState("");
  const [selectedBalancer, setSelectedBalancer] = useState("");
  const [selectedBalancerOptions, setSelectedBalancerOptions] = useState("");
  const [isNewRun, setIsNewRun] = useState(true);
  const [pipelineSteps, setPipelineSteps] = useState({});
  const [selectedPipelineSteps, setSelectedPipelineSteps] = useState({});
  const [defaultHyperparamSpaces, setDefaultHyperparamSpaces] = useState([]);
  const [sendEmail, setSendEmail] = useState(false);
  const [selectedDefaultParam, setSelectedDefaultParam] = useState([]);
  const [pipelineRunName, setPipelineRunName] = useState("");
  const [numTrials, setNumTrials] = useState(100);
  const [selectedModel, setSelectedModel] = useState("");
  const [modelHyperParams, setModelHyperParams] = useState({});
  const [showPresetForm, setShowPresetForm] = useState(false);
  const [originalNumTrials, setOriginalNumTrials] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [runNameError, setRunNameError] = useState("");
  const [isFoundationModel, setIsFoundationModel] = React.useState(false);
  // Error Snackbars
  const [snackbarSettings, setSnackbarSettings] = useState({
    isOpen: false,
    type: "success",
    message: "",
  });

  const fetchTrainConfig = (currentRunName = t("trainer.DropdownRunsNew")) => {
    let queryParam = "";
    if (currentRunName !== t("trainer.DropdownRunsNew")) {
      setIsNewRun(false);
      queryParam = `?run_name=${encodeURIComponent(currentRunName)}`;
    } else {
      setIsNewRun(true);
    }

    fetchData(
      `${process.env.REACT_APP_BACKEND_URL}/api/v0/dataset/${datasets[currentDatasetIdx].datasetName}/experiment/${experiments[currentExperimentIdx].experimentName}/run-config${queryParam}`
    ).then((data) => {
      const { runConfig, extendableRuns, defaultHyperparamPresets } = data;

      // Set values from runConfig
      setPipelineSteps(runConfig.availableAdditionalPipelineSteps);
      setSelectedPipelineSteps({});
      setHpOptimizers(runConfig.availableHpOptimizers);
      setDataBalancers(runConfig.availableDataBalancer);
      setDataBalancerOptions(runConfig.availableDataBalancerOptions);
      setSelectedOptimizer(runConfig.availableHpOptimizers[0]);
      setSelectedBalancer(runConfig.availableDataBalancer[0]);
      setSelectedBalancerOptions(runConfig.availableDataBalancerOptions.None);
      setPipelineRunName(runConfig.runName);
      setNumTrials(runConfig.numTrials);
      setModelHyperParams(runConfig.modelHyperParams);
      setSelectedModel(runConfig.modelType);
      setOriginalNumTrials(runConfig.numTrials);
      //  Set runsNames from extendableRuns
      const lastChildRunNames = extendableRuns.runs.reduce((acc, run) => {
        const lastChild = run.children[run.children.length - 1];
        return lastChild ? acc.concat(lastChild.runName) : acc;
      }, []);

      setRunsNames([t("trainer.DropdownRunsNew"), ...lastChildRunNames]);

      // From `defaultHyperparamPresets`
      setDefaultHyperparamSpaces(defaultHyperparamPresets);
    });
  };

  const handleBalancerChange = (event) => {
    setSelectedBalancer(event.target.value);
    setSelectedBalancerOptions(dataBalancerOptions[event.target.value]);
  };

  const PatternNotAllowed = ["_HPO", "\\?"];

  const handleRunNameChange = (event) => {
    const { value } = event.target;
    setPipelineRunName(value);

    if (value.trim() === "") {
      setRunNameError(t("trainer.RunNameNotEmpty"));
      return;
    }

    const forbiddenPattern = PatternNotAllowed.find((pattern) =>
      new RegExp(pattern).test(value)
    );

    if (forbiddenPattern) {
      setRunNameError(
        t("trainer.RunNameForbiddenPattern", { forbiddenPattern })
      );
    } else {
      setRunNameError("");
    }
  };

  const handleRunSelectorChange = (event) => {
    setSelectedRun(event.target.value);
    fetchTrainConfig(event.target.value);
  };
  const handleHyperParamsChange = (parentKey, paramKey, newParamSettings) => {
    setModelHyperParams({
      ...modelHyperParams,
      [selectedModel]: {
        ...modelHyperParams[selectedModel],
        [parentKey]: {
          ...modelHyperParams[selectedModel][parentKey],
          [paramKey]: newParamSettings,
        },
      },
    });
  };

  const handleOpenPresetForm = () => {
    if (!selectedModel || !modelHyperParams[selectedModel]) {
      alert("Please select a model first.");
      return;
    }
    setShowPresetForm(true);
  };

  const handlePipelineStepHyperParamsChange = (
    step,
    paramKey,
    newParamSettings
  ) => {
    // overwrite pipeline step hyperparams
    setSelectedPipelineSteps({
      ...selectedPipelineSteps,
      [step]: {
        ...selectedPipelineSteps[step],
        options: {
          ...selectedPipelineSteps[step].options,
          [paramKey]: newParamSettings,
        },
      },
    });
  };

  const handleDataBalancerOptionsChange = (_, paramKey, newParamSettings) => {
    setSelectedBalancerOptions((prevOptions) => ({
      ...prevOptions,
      [paramKey]: newParamSettings,
    }));
  };

  const handleOptiChange = (event) => {
    setSelectedOptimizer(event.target.value);
  };

  const handleModelChange = (modelType) => () => {
    if (modelType === "TabPFN") {
        setIsFoundationModel(true);
    }
    else {
        setIsFoundationModel(false);
    }
    setSelectedModel(modelType);
  };

  const handlePipelineStepSelection = (stepName) => () => {
    // make full copy of selectedPipelineSteps
    const newSelectedPipelineSteps = { ...selectedPipelineSteps };
    if (stepName in newSelectedPipelineSteps) {
      delete newSelectedPipelineSteps[stepName];
      if (stepName === "HPO") {
        setSelectedModel("");
      }
    } else {
      newSelectedPipelineSteps[stepName] = { ...pipelineSteps[stepName] };
      // remove not needed key
      delete newSelectedPipelineSteps[stepName].allowedModels;
      if (stepName === "HPO") {
        setSelectedModel(Object.keys(modelHyperParams)[0]);
      }
    }
    setSelectedPipelineSteps(newSelectedPipelineSteps);
  };

  const handleNumTrialsInputChange = (event) => {
    setNumTrials(event.target.value === "" ? "" : Number(event.target.value));
  };

  const handleBlur = (event) => {
    const newSteps = event.target.valueAsNumber;
    if (newSteps < 0) {
      setNumTrials(0);
    } else if (newSteps > 1000) {
      setNumTrials(1000);
    }
  };

  /* UI-Specific code to implement "ReadMore" behaviour */

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const handleStartPipeline = () => {
    // set snackbar notification, dependent on post status
    const dictCopy = { ...snackbarSettings };

    // construct pipeline steps list
    const allPipelineSteps = [];
    let numOfHPOSteps = 0;

    if (isNewRun) {
      // every new Run has the pipline step HPO
      numOfHPOSteps = 1;
      allPipelineSteps.push({
        stepName: `HPO01`,
        modelType: selectedModel,
        numTrials: isFoundationModel? 1: Number(numTrials),
        options: modelHyperParams[selectedModel],
      });

      // then push additional pipeline steps
      Object.entries(selectedPipelineSteps).forEach(([stepName, step]) => {
        allPipelineSteps.push({
          stepName: `${stepName}${(isNewRun ? 1 : 2)
            .toString()
            .padStart(2, "0")}`,
          options: { [stepName]: step.options },
          modelType: selectedModel,
        });
      });
    } else {
      // check if HPO is selected
      numOfHPOSteps = Object.keys(selectedPipelineSteps).includes("HPO")
        ? 1
        : 0;
      // add optional additional pipeline steps
      Object.entries(selectedPipelineSteps).forEach(([stepName, step]) => {
        allPipelineSteps.push({
          stepName: `${stepName}${(isNewRun ? 1 : 2)
            .toString()
            .padStart(2, "0")}`,
          options: { [stepName]: step.options },
          modelType: selectedModel,
        });
      });
    }

    // add additional HPO step, if AFS is in pipelineSteps
    // this HPO uses the same options as the last HPO step
    if (Object.keys(selectedPipelineSteps).includes("AFS")) {
      const HPONumber = numOfHPOSteps + 1;
      allPipelineSteps.push({
        stepName: `HPO${HPONumber.toString().padStart(2, "0")}`,
        modelType: selectedModel,
        numTrials: Number(numTrials),
        options: modelHyperParams[selectedModel],
      });
    }

    let parentRunName = pipelineRunName;
    if (!isNewRun) {
      parentRunName = pipelineRunName.substring(
        0,
        pipelineRunName.lastIndexOf("_")
      );
    }

    // add data selected Data Balancer into each step
    allPipelineSteps.forEach((step) => {
      step.dataBalancer = selectedBalancer;
      step.dataBalancerOptions = selectedBalancerOptions;
    });

    // then start processing: first rigger processing start via API
    postData(
      `${process.env.REACT_APP_BACKEND_URL}/api/v0/dataset/${datasets[currentDatasetIdx].datasetName}/experiment/${experiments[currentExperimentIdx].experimentName}/run`,
      {
        runName: parentRunName,
        isNewRun,
        hpOptimizer: selectedOptimizer,
        modelType: selectedModel,
        pipelineSteps: allPipelineSteps,
        sendEmail,
      }
    ).then((data) => {
      dictCopy.isOpen = true;
      dictCopy.message = data.data;
      if (data.status === 200) {
        dictCopy.type = "success";
        dictCopy.message = t("trainer.SuccessPipelineStarted");
        // if processing was started, change website state to process-view
        navigate(
          `/train/${datasets[currentDatasetIdx].datasetName}/${
            experiments[currentExperimentIdx].experimentName
          }/pipeline/${parentRunName}${isNewRun ? "?mode=new" : "?mode=extend"}`
        );
      } else {
        dictCopy.type = "error";
        if (data.status === 409) {
          dictCopy.message = t("trainer.ErrorRunAlreadyExists");
        }
      }
      setSnackbarSettings(dictCopy);
    });
  };

  const handleExtendRun = async () => {
    if (!pipelineRunName) {
      alert(t("trainer.SelectRunToExtend"));
      return;
    }

    if (Number(numTrials) < Number(originalNumTrials)) {
      alert(
        t("trainer.ErrorNumTrialsTooSmall", { previous: originalNumTrials })
      );
      return;
    }

    if (Number(numTrials) === Number(originalNumTrials)) {
      const confirmExtend = window.confirm(
        t("trainer.ConfirmExtendSameTrials", { trials: originalNumTrials })
      );
      if (!confirmExtend) {
        return;
      }
    }

    const parentRunName = pipelineRunName.substring(
      0,
      pipelineRunName.lastIndexOf("_")
    );
    const stepName = pipelineRunName.replace(`${parentRunName}_`, "");
    const payload = { [stepName]: { numTrials: Number(numTrials) } };

    const url = `${process.env.REACT_APP_BACKEND_URL}/api/v0/dataset/${datasets[currentDatasetIdx].datasetName}/experiment/${experiments[currentExperimentIdx].experimentName}/run/${parentRunName}/continue-run`;

    try {
      const response = await postData(url, payload);
      console.log("Response:", response);
      setSnackbarSettings({
        isOpen: true,
        type: "success",
        message: t("trainer.RunExtendedSuccess"),
      });

      navigate(
        `/train/${datasets[currentDatasetIdx].datasetName}/${experiments[currentExperimentIdx].experimentName}/pipeline/${parentRunName}?mode=extend`
      );
    } catch (err) {
      console.error(err);
      setSnackbarSettings({
        isOpen: true,
        type: "error",
        message: err?.response?.data?.detail || t("trainer.RunExtendFailed"),
      });
    }
  };

  // styles
  const stepCardStyles = {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    color: "black",
    justifyContent: "space-between",
    border: 0,
    borderRadius: "20px",
    backgroundColor: theme.palette.background.default,
    paddingBottom: `${theme.spacing(2)}`,
    transition: "background-color 200ms, box-shadow 200ms",
    "& .MuiTypography-body1": {
      color: "black",
    },
    "& .MuiSvgIcon-root": {
      color: theme.palette.primary.main,
    },
    "&:hover, &.selected": {
      backgroundColor: theme.palette.primary.light,
      border: 0,
      boxShadow: "2px 2px 5px #00000057",
      transition: "background-color 200ms, box-shadow 200ms",
      "& .MuiTypography-h5": {
        color: theme.palette.primary.dark,
        transition: "color 300ms",
      },
    },
  };
  // fetch Config Data by API requests, when the current dataset is gathered
  useEffect(() => {
    if (
      datasets.length > 0 &&
      experiments.length > 0 &&
      currentDatasetIdx >= 0 &&
      currentExperimentIdx >= 0
    ) {
      fetchTrainConfig();
    }
  }, [datasets, experiments, currentDatasetIdx, currentExperimentIdx]);

  return (
    <Grid id="trainer-container" container>
      <Grid
        container
        sx={{
          padding: theme.spacing(8, 8),
          borderRadius: "16px",
          backgroundColor: "white",
        }}
      >
        {
          // only render if current experiment has fulfilled all required steps and stats are loaded
          currentExperimentProgress?.processed ? (
            <>
              <Snackbar open={snackbarSettings.isOpen} autoHideDuration={8000}>
                <Alert severity={snackbarSettings.type}>
                  {snackbarSettings.message}
                </Alert>
              </Snackbar>

              <Grid item xl={12} lg={12} md={12} xs={12}>
                <Grid container spacing={2} rowSpacing={4}>
                  <Grid item lg={12} xs={12}>
                    <Typography variant="h4" gutterBottom>
                      {t("trainer.TitleRunCreation")}
                    </Typography>
                  </Grid>
                  <Grid item lg={3} xs={6}>
                    <FormControl>
                      <InputLabel>{t("trainer.DropdownRunsLabel")}</InputLabel>
                      <Select
                        displayEmpty
                        value={selectedRun}
                        label={t("trainer.DropdownRunsLabel")}
                        onChange={handleRunSelectorChange}
                        sx={{ minWidth: 200 }}
                        renderValue={(selected) => {
                          if (isNewRun) {
                            return <em>{t("trainer.DropdownRunsNew")}</em>;
                          }
                          return selected;
                        }}
                      >
                        {runsNames.map((name, index) => (
                          <MenuItem key={`run-selection-${index}`} value={name}>
                            {name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Tooltip title={t("trainer.HintDropdownRuns")}>
                      <IconButton>
                        <InfoIcon />
                      </IconButton>
                    </Tooltip>
                  </Grid>

                  <Grid item lg={9} xs={6}>
                    <FormControl>
                      <TextField
                        label={t("trainer.InputRunName")}
                        value={pipelineRunName}
                        onChange={handleRunNameChange}
                        disabled={!isNewRun}
                        error={Boolean(runNameError)}
                        helperText={runNameError}
                        sx={{
                          "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline":
                            {
                              borderColor: "red",
                            },
                        }}
                      />
                    </FormControl>
                    <Tooltip title={t("trainer.HintRunName")}>
                      <IconButton>
                        <InfoIcon />
                      </IconButton>
                    </Tooltip>
                  </Grid>

                  <Grid item lg={3} xs={6}>
                    <FormControl>
                      <InputLabel>
                        {t("trainer.DropdownBalancerMethod")}
                      </InputLabel>
                      <Select
                        value={selectedBalancer}
                        label={t("trainer.DropdownBalancerMethod")}
                        onChange={handleBalancerChange}
                        sx={{ minWidth: 200 }}
                      >
                        {dataBalancers.map((name) => (
                          <MenuItem key={name} value={name}>
                            {name === "None" ? (
                              <i>{t("trainer.SelectedNoDataBalancer")}</i>
                            ) : (
                              name
                            )}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Tooltip title={t("trainer.HintBalancerMethod")}>
                      <IconButton>
                        <InfoIcon />
                      </IconButton>
                    </Tooltip>
                  </Grid>

                  {isNewRun && (
                    <>
                      <Grid item lg={12} xs={12}>
                        <Typography variant="h4" gutterBottom>
                          {t("trainer.TitleTrainingConfiguration")}
                        </Typography>
                      </Grid>
                      <ModelCards
                        modelHyperParams={modelHyperParams}
                        handleModelChange={handleModelChange}
                        selectedModel={selectedModel}
                        isOptional={false}
                      />
                    </>
                  )}
                </Grid>

                {!isNewRun && (
                  <Divider textAlign="left" sx={{ margin: theme.spacing(2) }}>
                    {t("trainer.TitleExtendRun")}
                    <Tooltip title={t("trainer.HintPipelineSteps")}>
                      <IconButton>
                        <InfoIcon />
                      </IconButton>
                    </Tooltip>
                  </Divider>
                )}
                {/* ---------------------------If there is a new run then show Models-------------------------------- */}
                <Grid
                  container
                  spacing={2}
                  sx={{ marginTop: theme.spacing(2) }}
                >
                  {/* ---------------------------Extend Run Section ----------------------------*/}
                  {!isNewRun &&
                    !Object.keys(selectedPipelineSteps).includes("AFS") && (
                      <Grid
                        container
                        spacing={2}
                        sx={{
                          marginTop: theme.spacing(2),
                          alignItems: "center",
                        }}
                      >
                        <Grid item lg={3} xs={6}>
                          <InputLabel>
                            {t("trainer.SliderNumTrials")}
                            <Tooltip title={t("trainer.HintExtendNumTrials")}>
                              <IconButton>
                                <InfoIcon />
                              </IconButton>
                            </Tooltip>
                          </InputLabel>
                          <Slider
                            aria-label="Number of Trials Existing Run"
                            value={
                              typeof numTrials === "number" ? numTrials : 0
                            }
                            onChange={handleNumTrialsInputChange}
                            valueLabelDisplay="auto"
                            step={5}
                            marks
                            min={10}
                            max={1000}
                          />
                        </Grid>

                        <Grid item lg={2} xs={4}>
                          <Input
                            value={numTrials}
                            size="small"
                            onChange={handleNumTrialsInputChange}
                            onBlur={handleBlur}
                            inputProps={{
                              step: 5,
                              min: 4,
                              max: 1000,
                              type: "number",
                              "aria-labelledby": "input-slider-existing",
                            }}
                          />
                        </Grid>

                        <Grid item lg={2} xs={4}>
                          <Button
                            variant="contained"
                            color="primary"
                            size="medium"
                            onClick={handleExtendRun}
                            disabled={numTrials <= 0}
                          >
                            {t("trainer.ExtendRunButton")}
                          </Button>
                        </Grid>
                      </Grid>
                    )}

                  {(isNewRun ||
                    Object.keys(selectedPipelineSteps).includes("HPO")) && (
                    <>
                      <Grid item lg={3} xs={6}>
                        <InputLabel>
                          {t("trainer.SliderNumTrials")}
                          <Tooltip title={t("trainer.HintNumTrials")}>
                            <IconButton>
                              <InfoIcon />
                            </IconButton>
                          </Tooltip>
                        </InputLabel>
                        <Slider
                          aria-label="Number of Trials"
                          value={typeof numTrials === "number" ? numTrials : 0}
                          onChange={handleNumTrialsInputChange}
                          disabled={isFoundationModel}
                          valueLabelDisplay="auto"
                          step={5}
                          marks
                          min={10}
                          max={1000}
                        />
                      </Grid>

                      <Grid
                        item
                        lg={2}
                        xs={4}
                        sx={{ margin: theme.spacing(5, 0, 0, 3) }}
                      >
                        <Input
                          value={isFoundationModel ? 1 : numTrials}
                          size="small"
                          onChange={handleNumTrialsInputChange}
                          onBlur={handleBlur}
                          disabled={isFoundationModel}
                          inputProps={{
                            step: 5,
                            min: 4,
                            max: 1000,
                            type: "number",
                            "aria-labelledby": "input-slider",
                          }}
                        />
                      </Grid>
                      <Grid
                        item
                        lg={6}
                        xs={6}
                        sx={{ marginTop: theme.spacing(2) }}
                      >
                        <FormControl>
                          <InputLabel>
                            {t("trainer.DropdownOptiMethod")}
                          </InputLabel>
                          <Select
                            value={selectedOptimizer}
                            label={t("trainer.DropdownOptiMethod")}
                            disabled={isFoundationModel}
                            onChange={handleOptiChange}
                            sx={{ minWidth: 200 }}
                          >
                            {hpOptimizers.map((name) => (
                              <MenuItem key={name} value={name}>
                                {name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Tooltip title={t("trainer.HintOptiMethod")}>
                          <IconButton>
                            <InfoIcon />
                          </IconButton>
                        </Tooltip>
                      </Grid>
                    </>
                  )}
                </Grid>
                {/* ---------------- ----------------Automated Feature Selection ------------------------------ */}
                {Object.keys(pipelineSteps).some(
                  (step) =>
                    pipelineSteps[step].allowedModels.includes(selectedModel) ||
                    selectedModel === ""
                ) && (
                  // only render this section if current model has any available pipeline steps
                  <Grid
                    container
                    spacing={2}
                    sx={{ marginTop: theme.spacing(2) }}
                  >
                    <Grid item xs={12}>
                      <Divider textAlign="left">
                        {t("trainer.TitlePipelineSteps")}
                        <Tooltip title={t("trainer.HintPipelineSteps")}>
                          <IconButton>
                            <InfoIcon />
                          </IconButton>
                        </Tooltip>
                      </Divider>
                    </Grid>

                    {Object.keys(pipelineSteps).map(
                      (step) =>
                        (pipelineSteps[step].allowedModels.includes(
                          selectedModel
                        ) ||
                          selectedModel === "") && ( // Only render steps that are allowed for the selected model
                          <Grid
                            item
                            lg={8}
                            sx={{ display: "flex", alignItems: "stretch" }}
                            key={t(`trainer.Title${step.replace(/\s/g, "")}`)}
                          >
                            <Card sx={stepCardStyles}>
                              <CardContent>
                                <FormControlLabel
                                  sx={{
                                    margin: 0,
                                    width: "30px",
                                    transform: "translateX(-10px)",
                                  }}
                                  control={
                                    <Checkbox
                                      checked={Object.keys(
                                        selectedPipelineSteps
                                      ).includes(step)}
                                      onChange={handlePipelineStepSelection(
                                        step
                                      )}
                                    />
                                  }
                                />
                                <Typography
                                  variant="h6"
                                  gutterBottom
                                  sx={{ display: "inline" }}
                                >
                                  {t(`trainer.Title${step.replace(/\s/g, "")}`)}
                                </Typography>
                                <Typography color="text.secondary">
                                  {t(
                                    `trainer.Description${step.replace(
                                      /\s/g,
                                      ""
                                    )}`
                                  )}
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                        )
                    )}
                    <Grid item lg={12} md={12} xs={12}>
                      <Typography
                        variant="body1"
                        style={{
                          fontStyle: "italic",
                          color: theme.palette.primary.main,
                        }}
                        gutterBottom
                      >
                        {t(`trainer.HintCanBeExecutedLater`)}
                      </Typography>
                    </Grid>
                  </Grid>
                )}
                {/* --------------------------------------- Advanced Option Section ------------------------------------ */}
                <AdvancedOptionsSection
                  theme={theme}
                  t={t}
                  expanded={expanded}
                  handleExpandClick={handleExpandClick}
                  selectedModel={selectedModel}
                  defaultHyperparamSpaces={defaultHyperparamSpaces}
                  selectedDefaultParam={selectedDefaultParam}
                  handleOpenPresetForm={handleOpenPresetForm}
                  showPresetForm={showPresetForm}
                  modelHyperParams={modelHyperParams}
                  setShowPresetForm={setShowPresetForm}
                  setDefaultHyperparamSpaces={setDefaultHyperparamSpaces}
                  setSelectedDefaultParam={setSelectedDefaultParam}
                  selectedPipelineSteps={selectedPipelineSteps}
                  handleHyperParamsChange={handleHyperParamsChange}
                  handlePipelineStepHyperParamsChange={
                    handlePipelineStepHyperParamsChange
                  }
                  selectedBalancer={selectedBalancer}
                  selectedBalancerOptions={selectedBalancerOptions}
                  handleDataBalancerOptionsChange={
                    handleDataBalancerOptionsChange
                  }
                  handleStartPipeline={handleStartPipeline}
                  isNewRun={isNewRun}
                  fetchTrainConfig={fetchTrainConfig}
                  setModelHyperParams={setModelHyperParams}
                  setSelectedModel={setSelectedModel}
                />
                <Grid
                  item
                  xs={12}
                  sx={{ display: "flex", alignItems: "center", mb: 2 }}
                >
                  {showCheckbox && (
                    <>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={sendEmail}
                            onChange={(e) => setSendEmail(e.target.checked)}
                            sx={{
                              color: "#2879A8",
                              "&.Mui-checked": { color: "#2879A8" },
                            }}
                          />
                        }
                        label={
                          <Typography>
                            {t("trainer.SendEmailOnTrainingFinish")}
                          </Typography>
                        }
                      />
                      <a
                        href={`${process.env.REACT_APP_KC_SERVER_URL}realms/${process.env.REACT_APP_KC_REALM_ID}/account/?referrer=${process.env.REACT_APP_KC_CLIENT_ID}#/personal-info`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Tooltip title={t("trainer.HintSendEmail")}>
                          <IconButton>
                            <InfoIcon />
                          </IconButton>
                        </Tooltip>
                      </a>
                    </>
                  )}
                </Grid>

                <Grid item xs={4} sx={{ alignSelf: "end" }}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="medium"
                    onClick={handleStartPipeline}
                    disabled={
                      (Object.keys(selectedPipelineSteps).length === 0 &&
                        !isNewRun) ||
                      Boolean(runNameError)
                    }
                  >
                    {t("trainer.StartButton")}
                  </Button>
                </Grid>
              </Grid>
            </>
          ) : (
            // if user lands on this page, without having all required steps fulfilled, show hint
            <Grid item xs={12}>
              <Typography variant="h5" gutterBottom>
                {t("trainer.TitlePage")}
              </Typography>
              <Typography variant="body">
                {t("hints.NotYetProcessed")}
              </Typography>
            </Grid>
          )
        }
      </Grid>
    </Grid>
  );
}
